import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Store } from '@ngxs/store';

import { UnitTemplateGroup } from '../../models/unit-template-groups.model';
import { UnitTemplateActions } from './unit-template.actions';
import { UnitTemplateSelectors } from './unit-template.selectors';
import { ProjectUnitTemplateState } from './unit-template.state';

@Injectable({
  providedIn: 'root',
})
export class UnitTemplateFacade {
  filters$ = this.store.select(UnitTemplateSelectors.getFilters);
  unitsTemplateFilters$ = this.store.select(UnitTemplateSelectors.getUnitsTemplateFilters);
  unitsTemplate$ = this.store.select(UnitTemplateSelectors.getUnitsTemplate);
  groups$ = this.store.select(UnitTemplateSelectors.getUnitsTemplateGroups);
  selectedGroup$ = this.store.select(UnitTemplateSelectors.getSelectedGroups);
  isLoading$ = this.store
    .select(hasActionsExecuting([UnitTemplateActions.Load]))
    .pipe(debounceTime(150), distinctUntilChanged());

  constructor(
    private readonly store: Store,
    private readonly unitSelectionState: ProjectUnitTemplateState
  ) {}

  @Dispatch()
  load() {
    return new UnitTemplateActions.Load();
  }

  @Dispatch()
  loadUnitTemplateGroups() {
    return new UnitTemplateActions.Groups();
  }

  @Dispatch()
  addUnitTemplateGroup(name: string, isOrganization: boolean) {
    return new UnitTemplateActions.AddUnitTemplateGroup(name, isOrganization);
  }

  @Dispatch()
  updateUnitTemplateGroupName(groupId: string, description: string) {
    return new UnitTemplateActions.UpdateGroupTemplateName(groupId, description);
  }

  @Dispatch()
  moveUnit(unitTemplateId: string, groupId: string) {
    return new UnitTemplateActions.MoveUnit(unitTemplateId, groupId);
  }

  @Dispatch()
  updateUnitDescription(unitTemplateId: string, description: string) {
    return new UnitTemplateActions.UpdateUnitDescription(unitTemplateId, description);
  }

  @Dispatch()
  deleteUnitGroup(groupId: string) {
    return new UnitTemplateActions.DeleteGroupTemplate(groupId);
  }

  @Dispatch()
  setSelectedGroup(selectedGroup: UnitTemplateGroup) {
    return new UnitTemplateActions.SetSelectedGroup(selectedGroup);
  }

  @Dispatch()
  deleteUnitTemplate(uniteTemplateUuid: string) {
    return new UnitTemplateActions.Delete(uniteTemplateUuid);
  }

  @Dispatch()
  createUnitTemplate(unitId: string, unitTemplateName: string, groupId: string, alternativeId: string) {
    return new UnitTemplateActions.Create(unitId, unitTemplateName, groupId, alternativeId);
  }

  setFilter = (fieldId: string, value: string | number | null) => this.unitSelectionState.setFilter(fieldId, value);

  @Dispatch()
  resetState() {
    return new UnitTemplateActions.ClearState();
  }
}
